import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import SEO, { SEO_CONFIG } from '@/components/SEO';
import {
  Smile, Meh, Frown, Zap, Battery, BatteryLow, BatteryMedium, BatteryFull,
  Brain, CloudRain, Sun, Sparkles, Play, Check, Calendar, TrendingUp,
  Heart, Coffee, Moon, Target, ArrowRight, ChevronRight, Flame, ArrowLeft
} from 'lucide-react';

const API = '/api';

// Emoji scales for check-in
const moodOptions = [
  { value: 1, icon: Frown, label: 'Çok Kötü', color: 'text-red-500', bg: 'bg-red-50 hover:bg-red-100 border-red-200' },
  { value: 2, icon: Meh, label: 'Kötü', color: 'text-orange-500', bg: 'bg-orange-50 hover:bg-orange-100 border-orange-200' },
  { value: 3, icon: Meh, label: 'Normal', color: 'text-yellow-500', bg: 'bg-yellow-50 hover:bg-yellow-100 border-yellow-200' },
  { value: 4, icon: Smile, label: 'İyi', color: 'text-green-500', bg: 'bg-green-50 hover:bg-green-100 border-green-200' },
  { value: 5, icon: Smile, label: 'Harika', color: 'text-emerald-500', bg: 'bg-emerald-50 hover:bg-emerald-100 border-emerald-200' }
];

const energyOptions = [
  { value: 1, icon: BatteryLow, label: 'Çok Düşük', color: 'text-red-500', bg: 'bg-red-50 hover:bg-red-100 border-red-200' },
  { value: 2, icon: BatteryLow, label: 'Düşük', color: 'text-orange-500', bg: 'bg-orange-50 hover:bg-orange-100 border-orange-200' },
  { value: 3, icon: BatteryMedium, label: 'Orta', color: 'text-yellow-500', bg: 'bg-yellow-50 hover:bg-yellow-100 border-yellow-200' },
  { value: 4, icon: BatteryFull, label: 'Yüksek', color: 'text-green-500', bg: 'bg-green-50 hover:bg-green-100 border-green-200' },
  { value: 5, icon: Zap, label: 'Çok Yüksek', color: 'text-emerald-500', bg: 'bg-emerald-50 hover:bg-emerald-100 border-emerald-200' }
];

const stressOptions = [
  { value: 1, icon: Sun, label: 'Çok Düşük', color: 'text-emerald-500', bg: 'bg-emerald-50 hover:bg-emerald-100 border-emerald-200' },
  { value: 2, icon: Sun, label: 'Düşük', color: 'text-green-500', bg: 'bg-green-50 hover:bg-green-100 border-green-200' },
  { value: 3, icon: Brain, label: 'Orta', color: 'text-yellow-500', bg: 'bg-yellow-50 hover:bg-yellow-100 border-yellow-200' },
  { value: 4, icon: CloudRain, label: 'Yüksek', color: 'text-orange-500', bg: 'bg-orange-50 hover:bg-orange-100 border-orange-200' },
  { value: 5, icon: CloudRain, label: 'Çok Yüksek', color: 'text-red-500', bg: 'bg-red-50 hover:bg-red-100 border-red-200' }
];

export default function DailyCheckin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alreadyCheckedIn, setAlreadyCheckedIn] = useState(false);
  const [existingCheckin, setExistingCheckin] = useState(null);
  const [recommendedLesson, setRecommendedLesson] = useState(null);
  
  // Form state
  const [mood, setMood] = useState(null);
  const [energy, setEnergy] = useState(null);
  const [stress, setStress] = useState(null);
  const [notes, setNotes] = useState('');
  
  // Result state
  const [result, setResult] = useState(null);

  useEffect(() => {
    checkTodayStatus();
  }, []);

  const checkTodayStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(`${API}/checkin/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.checked_in) {
        setAlreadyCheckedIn(true);
        setExistingCheckin(response.data.checkin);
        setRecommendedLesson(response.data.recommended_lesson);
      }
    } catch (error) {
      console.error('Error checking status:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!mood || !energy || !stress) {
      toast.error('Lütfen tüm alanları doldurun');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/checkin/daily`,
        { mood, energy, stress, notes: notes || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.already_checked_in) {
        setAlreadyCheckedIn(true);
        setExistingCheckin(response.data.checkin);
        toast.info('Bugün zaten check-in yaptınız!');
      } else {
        setResult(response.data);
        setRecommendedLesson(response.data.recommended_lesson);
        toast.success('Check-in başarılı! 🎉');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Bir hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const renderScaleOption = (options, selectedValue, onSelect, label) => (
    <div className="mb-6">
      <h3 className="text-lg font-semibold mb-3 text-gray-700">{label}</h3>
      <div className="grid grid-cols-5 gap-2">
        {options.map((option) => {
          const Icon = option.icon;
          const isSelected = selectedValue === option.value;
          return (
            <button
              key={option.value}
              onClick={() => onSelect(option.value)}
              className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1
                ${isSelected 
                  ? `${option.bg} border-current ring-2 ring-offset-1 scale-105` 
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}
              `}
            >
              <Icon className={`h-8 w-8 ${isSelected ? option.color : 'text-gray-400'}`} />
              <span className={`text-xs font-medium ${isSelected ? option.color : 'text-gray-500'}`}>
                {option.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen pt-20 bg-gradient-to-b from-purple-50 to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // Already checked in - show result
  if (alreadyCheckedIn && existingCheckin) {
    return (
      <div className="min-h-screen pt-20 bg-gradient-to-b from-purple-50 via-white to-purple-50">
        <SEO {...SEO_CONFIG.dailyCheckin} />
        <div className="max-w-2xl mx-auto px-4 py-8">
          {/* Back Button */}
          <Button 
            variant="ghost" 
            className="mb-4 text-purple-600 hover:text-purple-800"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Geri
          </Button>
          
          {/* Success Header */}
          <div className="text-center mb-8">
            <div className="bg-green-100 p-4 rounded-full inline-block mb-4">
              <Check className="h-12 w-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Günlük Wellness Check-in</h1>
            <p className="text-green-600 font-semibold text-lg mb-1">Bugün Check-in Yaptınız! ✨</p>
            <p className="text-gray-600">İşte bugünkü durumunuz ve önerimiz</p>
          </div>

          {/* Today's Check-in Summary */}
          <Card className="mb-6 border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Calendar className="h-5 w-5 mr-2 text-purple-600" />
                Bugünkü Durumunuz
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 bg-white rounded-lg">
                  <div className="text-2xl mb-1">
                    {existingCheckin.mood >= 4 ? '😊' : existingCheckin.mood >= 3 ? '😐' : '😔'}
                  </div>
                  <div className="text-sm text-gray-500">Ruh Hali</div>
                  <div className="font-bold text-purple-600">{existingCheckin.mood}/5</div>
                </div>
                <div className="p-3 bg-white rounded-lg">
                  <div className="text-2xl mb-1">
                    {existingCheckin.energy >= 4 ? '⚡' : existingCheckin.energy >= 3 ? '🔋' : '🪫'}
                  </div>
                  <div className="text-sm text-gray-500">Enerji</div>
                  <div className="font-bold text-green-600">{existingCheckin.energy}/5</div>
                </div>
                <div className="p-3 bg-white rounded-lg">
                  <div className="text-2xl mb-1">
                    {existingCheckin.stress <= 2 ? '☀️' : existingCheckin.stress <= 3 ? '🧠' : '🌧️'}
                  </div>
                  <div className="text-sm text-gray-500">Stres</div>
                  <div className="font-bold text-orange-600">{existingCheckin.stress}/5</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* AI Recommendation */}
          {existingCheckin.ai_recommendation && (
            <Card className="mb-6 border-yellow-200 bg-gradient-to-r from-yellow-50 to-orange-50">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-yellow-200 p-3 rounded-full">
                    <Sparkles className="h-6 w-6 text-yellow-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-yellow-800 mb-2">AI Öneriniz</h3>
                    <p className="text-yellow-900">{existingCheckin.ai_recommendation}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recommended Lesson */}
          {recommendedLesson && (
            <Card className="mb-6 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
              <CardHeader>
                <CardTitle className="flex items-center text-green-800">
                  <Target className="h-5 w-5 mr-2" />
                  Bugün Önerilen Ders
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-lg">{recommendedLesson.title}</h4>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                      <Badge variant="secondary">{recommendedLesson.duration} dk</Badge>
                      <span>{recommendedLesson.category}</span>
                    </div>
                  </div>
                  <Button
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => navigate(`/lesson/${recommendedLesson.id}`)}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Derse Git
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions - Expanded with P1 features */}
          <div className="grid grid-cols-4 gap-3">
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => navigate('/activity')}
            >
              <Target className="h-6 w-6 text-blue-600" />
              <span className="text-xs">Aktivite</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => navigate('/sleep')}
            >
              <Moon className="h-6 w-6 text-indigo-600" />
              <span className="text-xs">Uyku</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => navigate('/streaks')}
            >
              <Flame className="h-6 w-6 text-orange-600" />
              <span className="text-xs">Streak</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => navigate('/ai-dietitian')}
            >
              <Coffee className="h-6 w-6 text-green-600" />
              <span className="text-xs">Diyetisyen</span>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show result after submitting
  if (result) {
    return (
      <div className="min-h-screen pt-20 bg-gradient-to-b from-purple-50 via-white to-purple-50">
        <SEO {...SEO_CONFIG.dailyCheckin} />
        <div className="max-w-2xl mx-auto px-4 py-8">
          {/* Success Header */}
          <div className="text-center mb-8">
            <div className="bg-green-100 p-4 rounded-full inline-block mb-4">
              <Sparkles className="h-12 w-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Günlük Wellness Check-in</h1>
            <p className="text-green-600 font-semibold text-lg mb-1">Check-in Tamamlandı! 🎉</p>
            <p className="text-gray-600">İşte size özel önerimiz</p>
          </div>

          {/* AI Recommendation */}
          {result.checkin?.ai_recommendation && (
            <Card className="mb-6 border-yellow-200 bg-gradient-to-r from-yellow-50 to-orange-50">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-yellow-200 p-3 rounded-full">
                    <Sparkles className="h-6 w-6 text-yellow-700" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-yellow-800 mb-2">Bugün için Öneriniz</h2>
                    <p className="text-yellow-900">{result.checkin.ai_recommendation}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recommended Lesson */}
          {result.recommended_lesson && (
            <Card className="mb-6 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50">
              <CardHeader>
                <CardTitle className="flex items-center text-green-800">
                  <Target className="h-5 w-5 mr-2" />
                  10 Dakikalık Önerilen Ders
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-lg">{result.recommended_lesson.title}</h4>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                      <Badge variant="secondary">{result.recommended_lesson.duration} dk</Badge>
                      <span>{result.recommended_lesson.category}</span>
                    </div>
                  </div>
                  <Button
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => navigate(`/lesson/${result.recommended_lesson.id}`)}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Derse Başla
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions - Expanded with P1 features */}
          <div className="grid grid-cols-4 gap-3">
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => navigate('/activity')}
            >
              <Target className="h-6 w-6 text-blue-600" />
              <span className="text-xs">Aktivite</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => navigate('/sleep')}
            >
              <Moon className="h-6 w-6 text-indigo-600" />
              <span className="text-xs">Uyku</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => navigate('/streaks')}
            >
              <Flame className="h-6 w-6 text-orange-600" />
              <span className="text-xs">Streak</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => navigate('/ai-dietitian')}
            >
              <Coffee className="h-6 w-6 text-green-600" />
              <span className="text-xs">Diyetisyen</span>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Check-in Form
  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-purple-50 via-white to-purple-50">
      <SEO {...SEO_CONFIG.dailyCheckin} />
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="mb-4 text-purple-600 hover:text-purple-800"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Geri
        </Button>
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="bg-purple-100 p-4 rounded-full inline-block mb-4">
            <Sun className="h-12 w-12 text-purple-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Günlük Wellness Check-in</h1>
          <p className="text-purple-600 font-medium">Günaydın! ☀️</p>
          <p className="text-gray-600">Bugün nasıl hissediyorsunuz?</p>
        </div>

        <Card className="border-purple-200">
          <CardContent className="p-6">
            {/* Mood */}
            <h2 className="sr-only">Check-in Formu</h2>
            {renderScaleOption(moodOptions, mood, setMood, '😊 Ruh Haliniz')}

            {/* Energy */}
            {renderScaleOption(energyOptions, energy, setEnergy, '⚡ Enerji Seviyeniz')}

            {/* Stress */}
            {renderScaleOption(stressOptions, stress, setStress, '🧠 Stres Seviyeniz')}

            {/* Notes */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 text-gray-700">📝 Eklemek istediğiniz not (opsiyonel)</h3>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Bugün nasıl hissediyorsunuz? Hedefleriniz neler?"
                className="resize-none"
                rows={3}
              />
            </div>

            {/* Submit */}
            <Button
              onClick={handleSubmit}
              disabled={submitting || !mood || !energy || !stress}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-lg py-6"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                  Analiz ediliyor...
                </>
              ) : (
                <>
                  <Sparkles className="h-5 w-5 mr-2" />
                  Check-in Yap & Öneri Al
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Info */}
        <p className="text-center text-sm text-gray-500 mt-4">
          AI, durumunuza göre size özel ders ve öneri sunacak
        </p>
      </div>
    </div>
  );
}
